/**
 * useTrackLineageEvents
 *
 * Builds the lineage timeline event list for a given track.
 * Reads from trackStore and sessionStore.
 *
 * Events derived:
 *   – track_created              : when the track was created
 *   – cycle_added                : parent cycle link (crosswise to cycle)
 *   – session_added              : sessions run under this track
 *   – track_official_session     : when the track's result was chosen
 *   – track_value_source_selected: when the value source was selected
 *   – track_ranking_locked       : when the ranking was locked
 */

import { useMemo } from 'react'
import { useTrackStore } from '../../../stores/prioritization/trackStore'
import { useSessionStore } from '../../../stores/prioritization/sessionStore'
import { toTimelineDate, type TimelineEvent } from './LineageTimeline'

function td(v: unknown) {
  return toTimelineDate(v)
}

/** Rows read loosely: older rows carry fields the typed models do not name. */
type Loose = Record<string, unknown>
const loose = (v: unknown): Loose => (v ?? {}) as Loose
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)

export function useTrackLineageEvents(trackId: string): TimelineEvent[] {
  const track = useTrackStore((s) => s.tracks.find((t) => t.id === trackId))
  const allSessions = useSessionStore((s) => s.sessions)

  return useMemo(() => {
    if (!track) return []

    const events: TimelineEvent[] = []

    // 1. Created
    const tr = loose(track)
    const createdAt = td(tr.createdAt)
    if (createdAt) {
      events.push({
        id: 'track-created',
        type: 'track_created',
        timestamp: createdAt,
        label: 'Track Created',
        detail: track.name,
        actor: str(tr.createdBy),
      })
    }

    // 2. Parent cycle link (crosswise)
    if (track.cycleId) {
      const ts = createdAt ?? new Date()
      events.push({
        id: `parent-cycle-${track.cycleId}`,
        type: 'cycle_added',
        timestamp: ts,
        label: 'Cycle Context',
        linkedId: track.cycleId,
        linkedLabel: 'View cycle',
        linkedPath: `/prioritization/cycles/${track.cycleId}`,
        isEstimated: !createdAt,
      })
    }

    // 3. Sessions run under this track
    const trackSessions = allSessions.filter(
      (s) =>
        loose(s).trackId === trackId ||
        loose(s).track_id === trackId,
    )
    trackSessions.forEach((session) => {
      const ss = loose(session)
      const ts = td(ss.createdAt)
      if (!ts) return
      events.push({
        id: `track-session-${session.id}`,
        type: 'session_added',
        timestamp: ts,
        label: str(ss.name) ?? 'Session Run',
        detail: ss.state ? `State: ${String(ss.state)}` : undefined,
        linkedId: session.id,
        linkedLabel: str(ss.name) ?? 'View session',
        linkedPath: `/prioritization/sessions/${session.id}`,
      })
    })

    // 4. Track result chosen (a published session's result)
    if (track.officialSessionId) {
      const officialSession = allSessions.find(
        (s) => s.id === track.officialSessionId,
      )
      const os = loose(officialSession)
      const ts =
        td(os.completedAt) ??
        td(os.createdAt) ??
        td(tr.officialSessionSetAt)
      if (ts) {
        events.push({
          id: `official-session-${track.officialSessionId}`,
          type: 'track_official_session',
          timestamp: ts,
          label: 'Track Result Chosen',
          detail: str(os.name) ?? track.officialSessionId.slice(0, 8),
          linkedId: track.officialSessionId,
          linkedLabel: str(os.name) ?? 'View session',
          linkedPath: `/prioritization/sessions/${track.officialSessionId}`,
        })
      }
    }

    // 5. Value source selected
    const valueSourceSelectedAt = td(tr.valueSourceSelectedAt)
    if (valueSourceSelectedAt) {
      events.push({
        id: 'value-source-selected',
        type: 'track_value_source_selected',
        timestamp: valueSourceSelectedAt,
        label: 'Value Source Selected',
        actor: str(tr.valueSourceSelectedBy),
      })
    }

    // 6. Ranking locked
    const rankingLockedAt = td(tr.rankingLockedAt)
    if (rankingLockedAt) {
      events.push({
        id: 'ranking-locked',
        type: 'track_ranking_locked',
        timestamp: rankingLockedAt,
        label: 'Ranking Locked',
        actor: str(tr.rankingLockedBy),
      })
    }

    // Dedupe + sort
    const seen = new Set<string>()
    return events
      .filter((e) => {
        if (seen.has(e.id)) return false
        seen.add(e.id)
        return true
      })
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [track, allSessions, trackId])
}
